/**
 * Sidebar navigation metadata — the SINGLE SOURCE OF TRUTH for both the
 * rendered sidebar (`components/layout/Layout.tsx`) and the route guards
 * (`components/RequirePerm.tsx`).
 *
 * Why this file exists (Plan-RBAC-SchemaRoles-2026-08.md §2, §6.1):
 * `App.tsx` guarded all 24 protected routes with `isAuthenticated` only. The
 * sidebar hid items by role, but nothing stopped a user typing the URL — e.g.
 * `Layout.tsx` restricts Domain Identities to `agents_manager` while a `viewer`
 * could reach `/domain-identities` and create or delete domains.
 *
 * 🚨 The invariant this module enforces: **the URL can never be more permissive
 * than the menu.** Both surfaces evaluate `isNavItemVisible()` against the same
 * `NAV_ITEMS` metadata, so a role list can never drift between them. Do not
 * re-declare an `allowed` list anywhere else.
 *
 * Routes with NO entry here are deliberately UNGUARDED — see
 * `components/RequirePerm.tsx` for the enumerated list and the reasoning.
 */
import {
  LayoutDashboard,
  Cpu,
  BookOpen,
  TestTube,
  Ticket,
  Users,
  Inbox,
  Contact,
  Plug,
  Globe,
  ShieldCheck,
  Bot,
  Settings,
  Wand2,
  Shirt,
  LayoutTemplate,
  Workflow,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { APP_CONFIG } from './appConfig';
import type { User } from '../types';
import { getVerbSet } from '../types/rbac';
import type { RolePermissions } from '../types/rbac';

/** Keys of the dark-ship hydration flags a nav item may be gated on. */
export type FeatureFlagKey = keyof NonNullable<User['features']>;

export interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  /**
   * Workspace roles that may see this item. `is_owner` and the `admin` role
   * bypass this list (see `isNavItemVisible`), mirroring the backend's
   * `_user_has_any_role` short-circuit.
   */
  allowed: string[];
  /**
   * Phase 1 permission key — Storm's snake_case schema ids. Under
   * `rbac_custom_roles` this, not `allowed`, decides visibility.
   *
   * ⚠️ `/virtual-try-on` deliberately shares the `stylist` key: it has no
   * schema of its own in the agreed 16 (it is parked, behind its own build
   * flag), and both are Shopify-only explainer pages with the same `allowed`
   * list. Granting Stylist therefore also grants Virtual Try-On.
   */
  schema: string;
  /** Item owns sub-routes — highlight on prefix match, not exact match. */
  prefix?: boolean;
  /** Stable `data-tour-target` anchor for SetupTour.tsx. */
  tourTarget?: string;
  /** Dropped entirely on the default (Stripe) build. */
  shopifyOnly?: boolean;
  /** Shown only when AT LEAST ONE of these hydration flags is true. */
  featureFlags?: FeatureFlagKey[];
  /**
   * 🚨 Never role-filtered. Plan §9.1 / §11.4 — a role matching zero nav items
   * filters the sidebar to `[]` and strands the user with nowhere to land.
   * Settings is the guaranteed landing spot (Profile is the other, and it is
   * rendered outside the nav list as the user card, so it needs no entry).
   */
  alwaysVisible?: boolean;
}

/** Shopify-only build — mirrors the check Layout.tsx used inline. */
const IS_SHOPIFY_BUILD = APP_CONFIG.billingProvider === 'shopify';

export const NAV_ITEMS: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', schema: 'dashboard', icon: LayoutDashboard, allowed: ['agents_manager', 'tickets_manager', 'viewer'], tourTarget: 'nav-dashboard' },
  // `tourTarget` exposes a stable data-tour-target on the sidebar Link
  // so the Shopify Setup Guide (SetupTour.tsx) can anchor its feature-
  // tour steps. Role-filtered items that drop out simply have no anchor
  // → the tour step gracefully falls back to anchorless (target: null).
  // Orchestrator hub — one unified assistant (2026-07-24): analytics,
  // knowledge management and agent building in a single chat. Writes always
  // go through a confirm card, so the surface stays readable by the same role
  // set as the dashboard rather than the tighter agents_manager gate. Keeps
  // prefix matching so the item still highlights on the legacy
  // /orchestrator/:tab URLs while they redirect.
  { name: 'Orchestrator', href: '/orchestrator', schema: 'orchestrator', icon: Workflow, allowed: ['agents_manager', 'tickets_manager', 'viewer'], prefix: true, tourTarget: 'nav-orchestrator' },
  // 🚨 NO `Reports` NAV ITEM, deliberately (2026-07-24). The Orchestrator
  // absorbed the surface: it answers the same analytics questions, renders
  // the same charts (one shared `VisualizationRenderer`), and opens the same
  // send-report dialog via the `open_surface` frame.
  //
  // ⚠️ `/reports` ITSELF STAYS ROUTED AND WORKING — do not delete the route,
  // and do NOT add a nav entry to "fix" its missing guard. Two reasons:
  // bookmarks must not 404, and it is the escape hatch for emailing a report
  // to an address that is NOT workspace staff or a saved contact. The
  // Orchestrator's own email tool is restricted to those two groups on purpose
  // (a model choosing a recipient is an exfiltration primitive), so `/reports`
  // is where a human goes when they need to send it somewhere else entirely.
  // Adding an entry here would guard the route and break that workflow.
  //
  // `/dashboard` above is a SEPARATE page and keeps its own nav item.
  { name: 'Inbox', href: '/inbox', schema: 'inbox', icon: Inbox, allowed: ['agents_manager', 'tickets_manager', 'viewer'], tourTarget: 'nav-inbox' },
  { name: 'Contacts', href: '/contacts', schema: 'contacts', icon: Contact, allowed: ['agents_manager', 'tickets_manager', 'viewer'], tourTarget: 'nav-contacts' },
  { name: 'Playground', href: '/playground', schema: 'playground', icon: TestTube, allowed: ['agents_manager'], tourTarget: 'nav-playground' },
  { name: 'Agent Avatar', href: '/agent-avatar', schema: 'agent_avatar', icon: Bot, allowed: ['agents_manager'] },
  { name: 'AI Agents', href: '/agents', schema: 'ai_agents', icon: Cpu, allowed: ['agents_manager', 'tickets_manager', 'viewer'] },
  // Templates — educational page for the Prodagi-curated starter packs.
  // `agents_manager` only, matching Knowledge Hub: applying a template
  // creates an agent AND knowledge articles, so it needs the same authority.
  // Route is flat (/templates, no sub-routes) so the exact-match active
  // state works without a prefix check.
  { name: 'Templates', href: '/templates', schema: 'templates', icon: LayoutTemplate, allowed: ['agents_manager'], tourTarget: 'nav-templates' },
  // AI Stylist marketing/explainer page — Shopify build only (dropped by
  // the shopifyOnly filter on non-Shopify builds).
  { name: 'Stylist', href: '/stylist', schema: 'stylist', icon: Wand2, allowed: ['agents_manager'], shopifyOnly: true },
  // Virtual Try-On educational page — Shopify build only (dropped by the
  // shopifyOnly filter on non-Shopify builds) AND hidden until the master
  // launch flag (VITE_VIRTUAL_TRYON_ENABLED) is on.
  { name: 'Virtual Try-On', href: '/virtual-try-on', schema: 'stylist', icon: Shirt, allowed: ['agents_manager'], shopifyOnly: true },
  { name: 'Knowledge Hub', href: '/knowledge', schema: 'knowledge_hub', icon: BookOpen, allowed: ['agents_manager'] },
  { name: 'Help Center', href: '/help-center', schema: 'help_center', icon: Globe, allowed: ['agents_manager'], tourTarget: 'nav-help-center' },
  { name: 'Integrations', href: '/integrations', schema: 'integrations', icon: Plug, allowed: ['agents_manager'], tourTarget: 'nav-integrations' },
  // Domain Identities — hub for connecting a branded domain: email sending
  // domains (Plan-DomainVerification §7a) + Help Center domain
  // (Plan-HelpCenterCustomDomain §P2). Dark-shipped: shown when ANY of these
  // runtime hydration flags is on (each mirrors its own backend env gate);
  // each section self-gates inside the page. Same authority as Integrations /
  // Help Center (agents_manager).
  { name: 'Domain Identities', href: '/domain-identities', schema: 'domain_identities', icon: ShieldCheck, allowed: ['agents_manager'], featureFlags: ['email_domains', 'help_center_custom_domain'] },
  { name: 'Tickets', href: '/tickets', schema: 'tickets', icon: Ticket, allowed: ['tickets_manager', 'viewer'] },
  { name: 'Team', href: '/team', schema: 'team', icon: Users, allowed: ['agents_manager', 'tickets_manager', 'viewer'], tourTarget: 'nav-team' },
  // Settings is per-device personal preferences (accessibility today). Available
  // to every authenticated user regardless of workspace role — and marked
  // `alwaysVisible` so it is the guaranteed landing spot for a role that
  // matches no other item (Plan §11.4).
  { name: 'Settings', href: '/settings', schema: 'settings', icon: Settings, allowed: ['agents_manager', 'tickets_manager', 'viewer'], alwaysVisible: true },
];

/** Context a visibility decision is evaluated against. */
export interface NavVisibilityContext {
  /** Effective workspace roles for the active workspace. */
  roles: string[];
  /** Effective owner flag for the active workspace. */
  isOwner: boolean;
  /** Hydration feature flags (`user.features`). */
  features?: User['features'];
  /**
   * Phase 1: the member's effective per-schema verbs, merged across the roles
   * they hold. Only consulted when `customRolesEnabled` is true.
   */
  permissions?: RolePermissions;
  /**
   * `rbac_custom_roles` — when true, visibility comes from
   * `permissions[schema].view` instead of the legacy `allowed` role list.
   *
   * 🚨 Must be false when the permissions could not be loaded. Evaluating an
   * empty permissions object as authoritative denies every page, which is the
   * confident-zero failure this project keeps hitting — see `usePermissions`.
   */
  customRolesEnabled?: boolean;
}

/**
 * The one predicate both the sidebar and the route guard call.
 *
 * Order mirrors the filter chain Layout.tsx has always applied:
 *   1. Shopify-only items dropped on the default build.
 *   2. Virtual Try-On hidden until its master launch flag is on.
 *   3. `featureFlags` items shown only when AT LEAST ONE flag is true.
 *   4. Role check — `alwaysVisible` items skip it entirely.
 *
 * 🚨 Feature-detection is fail-CLOSED on the flags (an absent key is falsy, so
 * a dark surface stays dark) and owner/admin short-circuit on the role check,
 * matching the backend's `_user_has_any_role`.
 */
export const isNavItemVisible = (
  item: NavItem,
  ctx: NavVisibilityContext,
): boolean => {
  if (item.shopifyOnly && !IS_SHOPIFY_BUILD) return false;
  if (item.href === '/virtual-try-on' && !APP_CONFIG.virtualTryonEnabled) return false;
  if (item.featureFlags && !item.featureFlags.some((f) => !!ctx.features?.[f])) {
    return false;
  }
  if (item.alwaysVisible) return true;
  // Owner and the `admin` preset short-circuit in both eras. Under custom roles
  // a "full access" role has every verb anyway, so this is only a fast path —
  // but it must stay, or a workspace owner with an empty membership role list
  // loses their own sidebar (plan §4a, §7).
  if (ctx.isOwner || ctx.roles.includes('admin')) return true;
  // Phase 1: the verb IS the permission. Phase 0 / flag-off: legacy role list.
  if (ctx.customRolesEnabled) return getVerbSet(ctx.permissions, item.schema).view;
  return item.allowed.some((role) => ctx.roles.includes(role));
};

/**
 * Resolve a pathname to its nav item, or `undefined` when the route has no nav
 * entry (⇒ no guard). Prefix items match their sub-routes so
 * `/orchestrator/chat` resolves to the Orchestrator entry.
 */
export const findNavItem = (pathname: string): NavItem | undefined =>
  NAV_ITEMS.find((item) =>
    item.prefix
      ? pathname === item.href || pathname.startsWith(`${item.href}/`)
      : pathname === item.href,
  );
